import React, { useContext, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import clsx from 'clsx';

import ColumnChart from 'components/charts/ColumnChart';
import DonutChart from 'components/charts/DonutChart';
import Dropdown from 'components/Dropdown';
import Searching from 'components/animations/Searching';

import { colors, moodColors } from 'styles';
import team from 'assets/images/team.png';
import hashtag from 'assets/images/hashtag.png';
import contact from 'assets/images/contact.png';
import mood from 'assets/images/mood.png';
import { ctx } from 'store';

import ActivityCard from '../ActivityCard';
import ActivityList from '../ActivityList';

import { useStyles } from './styles';

const ALL_CHANNELS = 'All-channels';

const RTWorkspace: React.FC = () => {
  const classes = useStyles();
  const [selected, setSelected] = useState(ALL_CHANNELS);
  const [channel, setChannel] = useState<any>(ALL_CHANNELS);

  const {
    data: { channelData, data, score, timeDataArray, memberData, moodData },
    teams: { currentTeam },
  } = useContext(ctx.store);

  const onChange = (value: string) => {
    setSelected(value);
    if (value !== ALL_CHANNELS) {
      setChannel(channelData.filter((channel: any) => channel.id === value)[0]);
    } else {
      setChannel(value);
    }
  };

  useEffect(() => {
    // Update the data for selected channel, every time data changes.
    if (channel !== ALL_CHANNELS) {
      setChannel(channelData.filter((c: any) => c.id === channel.id)[0]);
    }
  }, [data]);

  return (
    <>
      <Box display="flex" p={1} alignItems="center" className={classes.mainBox}>
        <Box p={1} flexGrow={1}>
          <Typography component="h1" variant="h5">
            Real Time
          </Typography>
        </Box>
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            addWorkspace
            data={channelData}
            translate={currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels}
            type="channels"
            setValue={onChange}
            value={selected}
            autocomplete
          />
        </Box>
      </Box>
      {data.length ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <Paper className={classes.paper}>
                <ActivityCard
                  image={team}
                  title={'Workspace Activity'}
                  data={channel === ALL_CHANNELS ? data.length : channel.count}
                />
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Paper className={classes.paper}>
                <ActivityCard
                  image={hashtag}
                  title={'Active Channels'}
                  data={channel === ALL_CHANNELS ? channelData.length : 1}
                />
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Paper className={classes.paper}>
                <ActivityCard
                  image={contact}
                  title={'Active Members'}
                  data={channel === ALL_CHANNELS ? memberData.length : channel.members.length}
                />
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Paper className={classes.paper}>
                <ActivityCard
                  image={mood}
                  title={'Overall Sentiment'}
                  data={
                    channel === ALL_CHANNELS
                      ? data.length > 0
                        ? score
                        : null
                      : channel
                      ? channel.score
                      : null
                  }
                  rt={true}
                  float={true}
                />
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} className={classes.metricGridItems}>
              <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                <Typography component="h2" className={classes.subtitle}>
                  Activity per Minute
                </Typography>
                <div className={classes.barChart}>
                  <ColumnChart
                    options={{
                      colors: [colors.blueBrand],
                      xaxis: {
                        title: {
                          text: 'Minutes ago',
                        },
                      },
                      yaxis: {
                        title: {
                          text: 'Activity',
                        },
                        labels: {
                          formatter: function (val: number) {
                            if (Number.isInteger(val)) {
                              return val;
                            }
                          },
                        },
                      },
                    }}
                    data={timeDataArray.map((element: any, index: number) => {
                      return {
                        x: index,
                        y:
                          channel === ALL_CHANNELS
                            ? element.length
                            : element.filter((act: any) => act.channel === channel.id).length,
                      };
                    })}
                    yName="Actividad"
                  />
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} className={classes.metricGridItems}>
              <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                <Typography component="h2" className={classes.subtitle}>
                  Total Activity
                </Typography>
                <div className={classes.activityDonutChart}>
                  <DonutChart
                    data={[
                      {
                        angle:
                          channel === ALL_CHANNELS
                            ? moodData.positive / moodData.total
                            : channel.moodData.positive / channel.moodData.total,
                        color: moodColors.positive,
                        label: 'POSITIVE',
                      },
                      {
                        angle:
                          channel === ALL_CHANNELS
                            ? moodData.neutral / moodData.total
                            : channel.moodData.neutral / channel.moodData.total,
                        color: moodColors.neutral,
                        label: 'NEUTRAL',
                      },
                      {
                        angle:
                          channel === ALL_CHANNELS
                            ? moodData.negative / moodData.total
                            : channel.moodData.negative / channel.moodData.total,
                        color: moodColors.negative,
                        label: 'NEGATIVE',
                      },
                    ]}
                  />
                </div>
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} className={classes.metricGridItems}>
              <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                <Typography component="h2" className={classes.subtitle}>
                  User Ranking
                </Typography>
                {channel === ALL_CHANNELS ? (
                  <div className={classes.listView}>
                    {currentTeam &&
                      memberData.map((element: any, index: number) => (
                        <ActivityList
                          key={`activity-list-team-${index}`}
                          data={element}
                          translate={
                            currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                          }
                          type="members"
                        />
                      ))}
                  </div>
                ) : (
                  <div className={classes.listView}>
                    {channel &&
                      channel.members.map((element: any, index: number) => (
                        <ActivityList
                          key={`activity-list-channel-${index}`}
                          data={element}
                          translate={
                            currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                          }
                          type="members"
                        />
                      ))}
                  </div>
                )}
              </Paper>
            </Grid>
            {channel === ALL_CHANNELS && (
              <Grid item xs={12} md={6} className={classes.metricGridItems}>
                <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                  <Typography component="h2" className={classes.subtitle}>
                    Channel Ranking
                  </Typography>
                  <div className={classes.listView}>
                    {currentTeam &&
                      channelData.map((element: any, index: number) => (
                        <ActivityList
                          key={`activity-list-channel-data-${index}`}
                          data={element}
                          translate={
                            currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels
                          }
                          type="channels"
                        />
                      ))}
                  </div>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        <div className={classes.noData}>
          <Typography component="h2">No data to be displayed in the last 10 minutes!</Typography>
          <Searching animationWidth={400} animationHeight={400} containerHeight="initial" />
        </div>
      )}
    </>
  );
};

export { RTWorkspace };
